import type { Tournament, TournamentOfChampions } from './types';

/**
 * Does `tournament` belong to one of `tournamentOfChampions`'s own rounds? Matched by
 * `tournamentGroupKey` — the single definition of "does TOC own this tournament", shared by
 * `TournamentOfChampionsStore.shouldRenderDialog` and `selectTournamentDialogVariant` so the two
 * can't quietly drift apart on what counts as a TOC-owned round.
 */
export const isTournamentOwnedByToc = (
  tournament: Tournament | null | undefined,
  tournamentOfChampions: TournamentOfChampions | null,
): boolean =>
  !!tournamentOfChampions &&
  !!tournament &&
  tournamentOfChampions.rounds.some((round) => round.tournamentGroupKey === tournament.tournamentGroupKey);

/**
 * Does TOC hold the player's single tournament slot (widget, dialog, auto-open)? Yes when nothing
 * else is seated, or the seat is one of its rounds. Also yes while TOC still owes the player a round
 * result (end-of-round screen, with or without a prize) or elimination Summary and the seat is a live
 * race: a race joined over an unacknowledged round parks
 * that round in `previous`, where nothing else offers it, while the race simply keeps running. A
 * finished, unclaimed race keeps the slot — that one has its own prize to claim (#2736).
 */
export const doesTocHoldTournamentSlot = ({
  currentTournament,
  tournamentOfChampions,
  hasPendingPopup,
}: {
  currentTournament: Tournament | null | undefined;
  tournamentOfChampions: TournamentOfChampions | null;
  hasPendingPopup: boolean;
}): boolean =>
  !currentTournament ||
  isTournamentOwnedByToc(currentTournament, tournamentOfChampions) ||
  (!!tournamentOfChampions && hasPendingPopup && currentTournament.status !== 'finished');
